import { Trans } from '@lingui/react/macro';
import { Box, Stack } from '@mui/material';
// We don't want to instrument errors in development
// eslint-disable-next-line no-restricted-imports
import Callout from '@watershed/ui-core/components/Callout';
import SectionedDefinitionList from '@watershed/ui-core/components/SectionedDefinitionList';
import InlineCode from '@watershed/ui-core/components/InlineCode';
import AirplaneIcon from '@watershed/icons/components/Airplane';
import WarningIcon from '@watershed/icons/components/Warning';
import isDashboardApp from '@watershed/ui-core/utils/isDashboardApp';
import { GraphQLError } from 'graphql';
import type { CombinedError } from 'urql';

export default function FailedToContactApiDevelopmentErrorBox({
  error,
}: {
  error?: CombinedError;
}) {
  return (
    <Box sx={{ height: '100vh', display: 'flex' }}>
      {error?.response?.status === 200 && error.graphQLErrors.length > 0 ? (
        <GraphqlErrorBox graphqlErrors={error.graphQLErrors} />
      ) : (
        <GenericErrorBox error={error} />
      )}
    </Box>
  );
}

function GraphqlErrorBox({
  graphqlErrors,
}: {
  graphqlErrors: Array<GraphQLError>;
}) {
  return (
    <Callout
      variant="error"
      IconComponent={WarningIcon}
      sx={{
        margin: 'auto',
        maxWidth: 1200,
      }}
      title={<Trans context="Header">GraphQL error</Trans>}
      description={
        <Stack gap={2}>
          {graphqlErrors.map((error, i) => (
            <SectionedDefinitionList
              key={i}
              sx={{ background: 'transparent', border: 'none' }}
            >
              <dt>
                <Trans context="Error message">Message</Trans>
              </dt>
              <dd>{error.message}</dd>
              <dt>
                <Trans context="Error path (GraphQL term)">Path</Trans>
              </dt>
              <dd>{error.path}</dd>
              <dt>
                <Trans context="Code referenced by technical error">Code</Trans>
              </dt>
              {/* TODO: Proper type for extensions -- GraphQL changed the type
            from any to unknown, which broke a few thing */}
              <dd>{(error.extensions as any).code ?? 'Unknown'}</dd>
              <dt>
                <Trans context="Technical term for error details">
                  Stacktrace
                </Trans>
              </dt>
              <dd>
                <pre>
                  {(error.extensions as any).exception?.stacktrace.join('\n') ??
                    '–'}
                </pre>
              </dd>
            </SectionedDefinitionList>
          ))}
        </Stack>
      }
    />
  );
}

function GenericErrorBox({ error }: { error?: CombinedError }) {
  const processName = isDashboardApp() ? 'dashboard-srv' : 'admin-srv';
  return (
    <Callout
      IconComponent={AirplaneIcon}
      variant="warning"
      sx={{
        margin: 'auto',
        maxWidth: 800,
      }}
      title={
        <Trans context="Technical error detail">
          The web frontend could not connect to the GraphQL API server. This
          could mean:
        </Trans>
      }
      description={
        <>
          <Box component="ol" sx={{ paddingLeft: 2, margin: 0 }}>
            <li>
              <Trans context="Technical error detail">
                The Next.js server is taking a long time to compile and restart,
                and you can refresh until it works 🤞.
              </Trans>
            </li>
            <li>
              <Trans context="Technical error detail">
                The <InlineCode>{processName}</InlineCode> process is not
                running, or has crashed. Check the output in your terminal.
                There could be a code error or you may need to install
                dependencies after switching branches or pulling code. Try{' '}
                <InlineCode>./scripts/install-deps</InlineCode>.
              </Trans>
            </li>
          </Box>
          {error?.response && (
            <Box sx={{ marginTop: 2 }}>
              <pre style={{ margin: 0 }}>
                <Trans context="Error detail UI">
                  Error: {error.response.statusText}
                </Trans>
              </pre>
              <pre style={{ margin: 0 }}>
                <Trans context="Error detail UI">
                  Status: {error.response.status}
                </Trans>
              </pre>
              <pre style={{ margin: 0 }}>
                <Trans context="Error detail UI">
                  URL: {error.response.url}
                </Trans>
              </pre>
            </Box>
          )}
        </>
      }
    />
  );
}
